import {Button, Table, Tag, Tooltip} from 'antd';
import moment from 'moment';
import {DOCUMENT_STATUS_COLORS} from 'pages/kybDashboard/kybConstants';
import {formatEnumLabel, personName} from 'pages/kybDashboard/kybUtils';
import {PROVENANCE_LABELS, isActiveDocument} from './kycHubUtils';
import styles from './kycHub.module.scss';

const day = value => (value ? moment(value).format('DD MMM YYYY') : '—');

/**
 * Hub documents, active and (when asked) historical.
 *
 * Archived and replaced rows are history: they show why they left the active
 * list and offer only View. `unlistedIds` marks KYB files no checklist row
 * claims, so an extra upload still reads as something to rule on.
 *
 * `editable` (Edit details) is separate from `modifiable` (Replace/Archive):
 * fixing a file's type or name is allowed in any KYB status.
 */
export const HubDocumentsTable = ({
  documents,
  typeLabel = formatEnumLabel,
  unlistedIds,
  reviewable,
  modifiable,
  editable,
  onPreview,
  canPreview,
  previewingId,
  onReview,
  onReplace,
  onArchive,
  onEditDetails,
  loading,
  pagination = false,
}) => {
  const columns = [
    {
      title: 'File',
      key: 'file',
      render: (_, record) => (
        <div className={styles.cell}>
          <span>{record.freeName || record.fileName || 'Unnamed file'}</span>
          <span className={styles.subtle}>
            {typeLabel(record.documentType)}
            {record.replacesDocumentId ? ' · new version' : ''}
            {isActiveDocument(record) && unlistedIds?.has(record.id)
              ? ' · not on the checklist'
              : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        // A replaced file is also stamped `deletedAt`, so check this first.
        if (record.replacedByDocumentId) {
          return (
            <div className={styles.cell}>
              <Tag>Replaced</Tag>
              <span className={styles.subtle}>{day(record.replacedAt)}</span>
            </div>
          );
        }
        if (record.deletedAt) {
          return (
            <div className={styles.cell}>
              <Tag>Archived</Tag>
              <span className={styles.subtle}>
                {day(record.deletedAt)}
                {record.deletionReason ? ` · ${record.deletionReason}` : ''}
              </span>
            </div>
          );
        }
        return (
          <div className={styles.cell}>
            <Tag color={DOCUMENT_STATUS_COLORS[record.status] || 'default'}>
              {formatEnumLabel(record.status)}
            </Tag>
            {record.reviewerNote && (
              <Tooltip title={record.reviewerNote}>
                <span className={styles.subtle}>Note attached</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Uploaded',
      key: 'uploaded',
      render: (_, record) => (
        <div className={styles.cell}>
          <span>{day(record.createdAt)}</span>
          <span className={styles.subtle}>
            {[
              PROVENANCE_LABELS[record.provenance],
              record.uploadedBy && `by ${personName(record.uploadedBy)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {record.providedAt && (
            <span className={styles.subtle}>
              Provided {day(record.providedAt)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Reviewed',
      key: 'reviewed',
      render: (_, record) =>
        record.reviewedAt ? (
          <div className={styles.cell}>
            <span>{day(record.reviewedAt)}</span>
            <span className={styles.subtle}>
              {personName(record.reviewedBy)}
            </span>
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => {
        const openable = canPreview(record);
        const active = isActiveDocument(record);
        return (
          <div className={styles.rowActions}>
            <Tooltip
              title={
                openable ? '' : 'This file has no usable storage link to open.'
              }>
              <Button
                size="small"
                disabled={!openable}
                loading={previewingId === record.id}
                onClick={() => onPreview(record)}>
                View
              </Button>
            </Tooltip>
            {active && reviewable && record.status === 'PENDING' && (
              <Button
                size="small"
                type="primary"
                onClick={() => onReview(record)}>
                Review
              </Button>
            )}
            {active && editable && onEditDetails && (
              <Button size="small" onClick={() => onEditDetails(record)}>
                Edit details
              </Button>
            )}
            {active && modifiable && (
              <>
                <Button size="small" onClick={() => onReplace(record)}>
                  Replace
                </Button>
                <Button size="small" danger onClick={() => onArchive(record)}>
                  Archive
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={documents}
      loading={loading}
      pagination={pagination}
      scroll={{x: true}}
      locale={{emptyText: 'No documents.'}}
    />
  );
};
